import { SiteLayout } from "@/components/layout/SiteLayout";
import { RelatedPages } from "@/components/RelatedPages";
import { SEO } from "@/components/SEO";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { NetworkRouter } from "@/components/NetworkRouter";
import { motion, type Variants } from "framer-motion";
import { breadcrumbSchema, personSchema, webPageSchema } from "@/lib/seo";
import { withNetworkUtm } from "@/lib/network";
import { NetworkNextStep } from "@/components/NetworkNextStep";
import { AlsoOnShrinkopedia } from "@/components/AlsoOnShrinkopedia";

const fadeInUp: Variants = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } },
};

const reviewerPersonSchema = {
  "@context": "https://schema.org",
  "@type": "Person",
  name: "Shariq Refai",
  honorificSuffix: "MD, MBA, FAPA",
  jobTitle: "Board-certified Psychiatrist",
  url: "https://shariqrefai.com/medical-reviewer/",
  image: "https://shariqrefai.com/img/headshot.webp",
  identifier: [
    { "@type": "PropertyValue", propertyID: "NPI", value: "1467680660" },
    {
      "@type": "PropertyValue",
      propertyID: "ORCID",
      value: "0009-0009-1090-4373",
    },
    { "@type": "PropertyValue", propertyID: "Wikidata", value: "Q139822307" },
  ],
  sameAs: [
    "https://www.wikidata.org/wiki/Q139822307",
    "https://orcid.org/0009-0009-1090-4373",
    "https://www.linkedin.com/in/shariqrefai",
  ],
  worksFor: {
    "@type": "Organization",
    name: "shrinkMD",
    url: "https://shrinkmd.com/",
  },
  alumniOf: { "@type": "Organization", name: "Duke University" },
  knowsAbout: [
    "Psychiatry",
    "Anxiety disorders",
    "Sports and performance psychiatry",
    "Telepsychiatry",
    "Evidence-based pharmacology",
    "Mental health literacy",
  ],
};

export default function MedicalReviewer() {
  const SEO_TITLE = "Medical Reviewer Profile | Shariq Refai, MD, MBA";
  const SEO_DESCRIPTION =
    "Medical review profile for Shariq Refai, MD, MBA. Board-certified psychiatrist, founder of shrinkMD, editorial reviewer for The Shrink Network.";

  const breadcrumbs = [
    { name: "Home", path: "/" },
    { name: "Medical Reviewer Profile", path: "/medical-reviewer" },
  ];

  return (
    <SiteLayout>
      <SEO
        title={SEO_TITLE}
        description={SEO_DESCRIPTION}
        url="/medical-reviewer"
        image="/og/medical-reviewer.jpg"
        keywords="Shariq Refai medical reviewer, board-certified psychiatrist, medical editor, The Shrink Network, NPI 1467680660, ORCID, fact-checked mental health"
        schema={[
          reviewerPersonSchema,
          personSchema,
          webPageSchema({
            path: "/medical-reviewer",
            title: SEO_TITLE,
            description: SEO_DESCRIPTION,
            breadcrumbs,
            speakableSelectors: [".reviewer-bio"],
          }),
          breadcrumbSchema(breadcrumbs),
        ]}
      />

      {/* Intro */}
      <section className="pt-24 pb-12 md:pt-32 md:pb-16 px-4 md:px-8 bg-muted/30 border-b border-border/50">
        <div className="container mx-auto max-w-3xl">
          <Breadcrumbs
            items={[
              { name: "Home", href: "/" },
              { name: "Medical Reviewer Profile" },
            ]}
          />
          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeInUp}
            className="flex flex-col gap-5"
          >
            {/* Credibility eyebrow (Master Brief hero anatomy) */}
            <span className="text-accent text-xs font-bold tracking-[0.25em] uppercase">
              Medical Reviewer · Shrink Network · Board-Certified Psychiatrist
            </span>
            {/* Outcome-led H1: answers the reader's real question about what review means */}
            <h1 className="text-4xl md:text-5xl font-serif text-primary leading-tight tracking-tight">
              The named clinical reviewer for the Shrink Network's editorial content.
            </h1>
            {/* Subhead: mechanism + scope */}
            <p className="reviewer-bio text-lg md:text-xl text-secondary font-light leading-relaxed">
              Every clinical claim on Shrinkopedia, AnxietyResource, DepressionResource, AnxietyResearch, PsychiatryRx, and ShrinkDaily is reviewed against DSM-5-TR, current guidelines, and primary sources before publication.
            </p>
            {/* Proof strip */}
            <ul aria-label="Credentials" className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs md:text-sm text-muted-foreground list-none p-0">
              <li className="flex items-center gap-2"><span aria-hidden="true" className="text-accent">✓</span>ABPN board certified</li>
              <li aria-hidden="true" className="text-border">·</li>
              <li className="flex items-center gap-2"><span aria-hidden="true" className="text-accent">✓</span>FAPA</li>
              <li aria-hidden="true" className="text-border">·</li>
              <li className="flex items-center gap-2"><span aria-hidden="true" className="text-accent">✓</span>NPI 1467680660</li>
              <li aria-hidden="true" className="text-border">·</li>
              <li className="flex items-center gap-2"><span aria-hidden="true" className="text-accent">✓</span>ORCID 0009-0009-1090-4373</li>
            </ul>
          </motion.div>
        </div>
      </section>

      {/* Body */}
      <article className="py-16 md:py-20 px-4 md:px-8 bg-background">
        <div className="container mx-auto max-w-3xl prose prose-lg max-w-none prose-headings:font-serif prose-headings:font-normal prose-headings:text-primary prose-h2:text-2xl md:prose-h2:text-3xl prose-h2:mt-12 prose-h2:mb-4 prose-p:font-light prose-p:text-secondary prose-p:leading-relaxed prose-li:font-light prose-li:text-secondary prose-strong:text-primary prose-strong:font-medium prose-a:text-accent hover:prose-a:text-primary">
          <h2>Credentials</h2>
          <ul>
            <li>Board certified in psychiatry by the American Board of Psychiatry and Neurology.</li>
            <li>Board certified in sports and performance psychiatry.</li>
            <li>Fellow of the American Psychiatric Association (FAPA).</li>
            <li>Doctor of Medicine.</li>
            <li>Master of Business Administration.</li>
            <li>Active medical license. NPI: 1467680660.</li>
            <li>ORCID: 0009-0009-1090-4373.</li>
            <li>Wikidata: Q139822307.</li>
          </ul>

          <h2>Practice</h2>
          <p>I'm a practicing psychiatrist and the founder of shrinkMD, a multistate telepsychiatry practice. I've worked in psychiatry for more than fifteen years across general adult psychiatry, sports and performance psychiatry, and addiction medicine, with experience in professional sports settings including affiliations involving the NFL Substance Abuse Program and the Jacksonville Jaguars.</p>

          <h2>Editorial role</h2>
          <p>I'm the medical editor and a contributing author for the editorial publications in <a href={withNetworkUtm("https://shrinknetwork.com/", "medical_reviewer_page")} rel="noopener" className="text-accent underline decoration-accent/40 underline-offset-4 hover:decoration-accent transition-colors">The Shrink Network</a>: <a href={withNetworkUtm("https://shrinkopedia.com/authors/shariq-refai/", "medical_reviewer_author_profile")} target="_blank" rel="noopener noreferrer" className="text-accent underline decoration-accent/40 underline-offset-4 hover:decoration-accent transition-colors">Shrinkopedia</a>, AnxietyResource, DepressionResource, AnxietyResearch, PsychiatryRx, and ShrinkDaily. I review the clinical content on every page before publication. I also author the evidence summaries, condition entries, and treatment overviews where my name appears.</p>

          <h2>What "Reviewed by Shariq Refai, MD, MBA" means</h2>
          <p>When a page on the network carries the line "Reviewed by Shariq Refai, MD, MBA," it means I read the page for clinical accuracy and editorial consistency before it went live, and I'm willing to put my name on what it says. The review process is described in detail in the editorial methodology pages of each property.</p>

          <h2>What it doesn't mean</h2>
          <p>Reading a page I reviewed does not make you my patient. The educational publications in the network aren't a clinical service. Reviewing a page doesn't establish a doctor-patient relationship with the reader. If you're looking for clinical care, the right destination is shrinkMD or your own clinician.</p>

          <h2>Areas I cover most often</h2>
          <p>Anxiety disorders, panic, social anxiety, OCD-spectrum conditions, generalized anxiety, mood disorders including major depressive disorder and bipolar disorder, sleep disorders, sports and performance psychiatry, modern psychiatric practice, telepsychiatry, evidence-based pharmacology, and the limits of popular mental health commentary.</p>

          <h2>Corrections and contact</h2>
          <p>If you spot something wrong on a page I reviewed, I want to know. Each site has a corrections page that routes to the right place. The fastest path to me directly is the contact form on this site with "Editorial Correction" or "Medical Review" selected.</p>

          <h2>Disclosures</h2>
          <p>I'm the founder of shrinkMD (clinical practice), shrinQ (commercial educational program), and Unstuck and QuitScrolling (wellness apps). I'm the author of three books on overthinking and anxiety. The editorial publications I review don't run ads, take affiliate commissions, or accept sponsored content. Where I have a financial interest related to a topic, it's disclosed on the relevant page.</p>
        </div>
      </article>

      {/* Continue */}
      <section className="py-16 md:py-20 px-4 md:px-8 bg-muted/30 border-t border-border/50">
        <div className="container mx-auto max-w-3xl">
          <NetworkRouter
            eyebrow="Continue"
            cards={[
              { label: "Read the founder narrative", href: "/founder-of-the-shrink-network" },
              { label: "See the project list", href: "/projects" },
              { label: "Read the books", href: "/books" },
              { label: "Contact for media or corrections", href: "/contact" },
            ]}
          />
        </div>
      </section>
    
      <section className="bg-background py-20 border-t border-border">
        <div className="container mx-auto px-4 md:px-8 max-w-4xl">
          <NetworkNextStep
            campaign="medical_reviewer_next_step"
            destinations={["shrinkmd", "shrinkopedia", "psychiatryrx"]}
          />
        </div>
      </section>
    
      <section className="bg-background py-16 md:py-20 px-4 md:px-8 border-t border-border">
        <div className="container mx-auto max-w-3xl">
          <AlsoOnShrinkopedia slug="medical-reviewer" variant="credentialing" />
        </div>
      </section>
      <RelatedPages
        entries={[
          { href: "/medical-review-board", label: "Medical review board", why: "Who reviews clinical content and how reviewers are selected." },
          { href: "/editorial-process", label: "Editorial process & standards", why: "How essays and educational content are authored and reviewed." },
          { href: "/evidence", label: "How evidence is evaluated", why: "The evidence hierarchy behind the guidance published here." },
          { href: "/corrections", label: "Corrections & updates policy", why: "How factual errors are handled and how updates are logged." },
        ]}
      />
    </SiteLayout>
  );
}
